import React from 'react';
import type {
  CallChannelState,
  CapabilitiesState,
  CapChannelId,
  ChatChannelState,
  EmailChannelState,
  SmsChannelState,
  VerificationState,
  WarmTransferRule,
} from '../../receptionist-settings/types';
import {
  RCPT_SETTINGS_CAP_ACTIONS_FLAT,
  RCPT_SETTINGS_CAP_GROUPS,
  RCPT_SETTINGS_VERIFICATION_METHODS,
} from '../../receptionist-settings/mockData';
import { Icon } from '../Icon/Icon';
import {
  SettingsFieldRow,
  SettingsPageHeader,
  SettingsSectionHeader,
  SettingsSelect,
  SettingsToggle,
} from './SettingsShared';

type WarmTransferKind = 'call' | 'sms-triage' | 'email-silent' | 'chat-triage';

function ChannelCapSlice({
  channelId,
  channelLabel,
  capabilitiesState,
  setCapabilitiesState,
}: {
  channelId: CapChannelId;
  channelLabel: string;
  capabilitiesState: CapabilitiesState;
  setCapabilitiesState: React.Dispatch<React.SetStateAction<CapabilitiesState>>;
}) {
  const emptyChannels = (): Record<CapChannelId, boolean> => ({
    call: false,
    sms: false,
    email: false,
    chat: false,
  });

  const toggle = (actionId: string) =>
    setCapabilitiesState((s) => {
      const current = s.actions[actionId] ?? emptyChannels();
      return {
        ...s,
        actions: {
          ...s.actions,
          [actionId]: { ...current, [channelId]: !current[channelId] },
        },
      };
    });

  const enabledCount = RCPT_SETTINGS_CAP_ACTIONS_FLAT.filter(
    (id) => capabilitiesState.actions[id]?.[channelId],
  ).length;

  return (
    <div className="set-card">
      <SettingsSectionHeader
        num="01"
        title={`What Elva can do on ${channelLabel}`}
        sub={`${enabledCount} of ${RCPT_SETTINGS_CAP_ACTIONS_FLAT.length} actions enabled. Edits here also update the master matrix in Capabilities.`}
        action={{ label: `${enabledCount} on` }}
      />
      {RCPT_SETTINGS_CAP_GROUPS.map((group, gi) => (
        <div key={group.id}>
          <div
            className={[
              'border-b border-[var(--cream-2)] px-6 py-3',
              gi === 0 ? '' : 'border-t border-[var(--cream-2)]',
            ].join(' ')}
            style={{ background: 'var(--surface-mute)' }}
          >
            <div className="set-eyebrow text-[9.5px]">
              {group.num} · {group.title}
            </div>
          </div>
          {group.actions.map((action, ai) => {
            const enabled = capabilitiesState.actions[action.id]?.[channelId] ?? false;
            const isLast = gi === RCPT_SETTINGS_CAP_GROUPS.length - 1 && ai === group.actions.length - 1;
            return (
              <div
                key={action.id}
                className={[
                  'flex items-center justify-between gap-3.5 px-6 py-[13px]',
                  isLast ? '' : 'border-b border-[var(--cream-2)]',
                ].join(' ')}
              >
                <div className="min-w-0">
                  <div className="mb-px text-[13.5px] font-medium tracking-[-0.005em] text-[var(--ink-0)]">
                    {action.label}
                  </div>
                  <div className="text-[11.5px] tracking-[-0.005em] text-[var(--ink-2)]">{action.note}</div>
                </div>
                <SettingsToggle value={enabled} onChange={() => toggle(action.id)} />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ChannelVerification({
  state,
  setState,
  initial,
  channelLabel,
}: {
  state: VerificationState;
  setState: (v: VerificationState) => void;
  initial: VerificationState;
  channelLabel: string;
}) {
  const upd = (key: keyof VerificationState, value: boolean) => setState({ ...state, [key]: value });
  const dirty = (key: keyof VerificationState) => state[key] !== initial[key];
  const enabledCount = RCPT_SETTINGS_VERIFICATION_METHODS.filter((m) => state[m.id]).length;

  const summary =
    enabledCount === 0
      ? `No verifiers enabled — Elva can't verify identity on ${channelLabel}.`
      : `${enabledCount} verifier${enabledCount === 1 ? '' : 's'} enabled. Elva needs name + at least one to share chart info.`;

  return (
    <div className="set-card">
      <SettingsSectionHeader
        num="02"
        title={`How Elva verifies ${channelLabel} patients`}
        sub={summary}
      />
      {RCPT_SETTINGS_VERIFICATION_METHODS.map((m, i) => (
        <SettingsFieldRow
          key={m.id}
          title={m.label}
          description={m.desc}
          isDirty={dirty(m.id)}
          last={i === RCPT_SETTINGS_VERIFICATION_METHODS.length - 1}
        >
          <SettingsToggle value={state[m.id]} onChange={(v) => upd(m.id, v)} />
        </SettingsFieldRow>
      ))}
    </div>
  );
}

function WarmTransferRules({
  rules,
  setRules,
  kind,
  num = '03',
}: {
  rules: WarmTransferRule[];
  setRules: (rules: WarmTransferRule[]) => void;
  kind: WarmTransferKind;
  num?: string;
}) {
  const KIND_COPY: Record<
    WarmTransferKind,
    { title: string; sub: string; addLabel: string; hasPhone: boolean }
  > = {
    call: {
      title: 'When Elva hands the call to a human',
      sub: 'Each rule lists a topic Elva watches for and the line she transfers to. Elva briefs the receiver before connecting.',
      addLabel: 'Add transfer rule',
      hasPhone: true,
    },
    'sms-triage': {
      title: 'When Elva escalates an SMS thread to staff',
      sub: "Elva keeps responding herself, but creates a Triage Inbox note when a rule matches. No automatic handoff message is sent.",
      addLabel: 'Add escalation rule',
      hasPhone: false,
    },
    'email-silent': {
      title: 'When Elva escalates an email to staff',
      sub: "Email isn't real-time. When a rule matches, Elva creates a Triage Inbox note staff reviews; no patient escalation message is sent.",
      addLabel: 'Add escalation rule',
      hasPhone: false,
    },
    'chat-triage': {
      title: 'When Elva pulls in a human teammate',
      sub: 'Chat is real-time but lower-stakes than phone. Elva sends a friendly transition message and routes to the live-chat team.',
      addLabel: 'Add transfer rule',
      hasPhone: false,
    },
  };
  const cfg = KIND_COPY[kind];

  const updateRule = (id: number, key: keyof WarmTransferRule, value: string) =>
    setRules(rules.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  const removeRule = (id: number) => setRules(rules.filter((r) => r.id !== id));
  const addRule = () => {
    const nextId = (rules.reduce((m, r) => Math.max(m, r.id), 0) || 0) + 1;
    const newRule: WarmTransferRule = cfg.hasPhone
      ? { id: nextId, topic: '', phoneNumber: '' }
      : { id: nextId, topic: '' };
    setRules([...rules, newRule]);
  };

  return (
    <div className="set-card">
      <SettingsSectionHeader num={num} title={cfg.title} sub={cfg.sub} railColor="var(--set-warning)" />
      <div className="px-6 py-[18px]">
        {rules.length === 0 ? (
          <div className="rounded-[10px] border border-dashed border-[var(--cream-2)] bg-[var(--surface-mute)] px-4 py-5 text-center text-[12.5px] text-[var(--ink-2)]">
            No rules yet. Elva won't escalate on this channel until you add at least one.
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {rules.map((rule, i) => (
              <div
                key={rule.id}
                className="flex flex-col gap-2.5 rounded-[11px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-3.5 shadow-[var(--sh-1)]"
              >
                <div className="flex items-center justify-between">
                  <span className="font-[var(--font-mono)] text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--ink-2)]">
                    Rule {String(i + 1).padStart(2, '0')}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRule(rule.id)}
                    className="inline-flex cursor-pointer items-center rounded-md border-0 bg-transparent p-1 text-[var(--ink-3)] hover:text-[var(--set-negative)]"
                  >
                    <Icon kind="trash" size={13} stroke={1.6} />
                  </button>
                </div>
                <textarea
                  className="set-textarea min-h-[56px] text-[13.5px]"
                  value={rule.topic}
                  onChange={(e) => updateRule(rule.id, 'topic', e.target.value)}
                  placeholder="When the patient mentions…"
                  rows={2}
                />
                {cfg.hasPhone ? (
                  <input
                    className="set-input w-[280px] font-[var(--font-mono)] font-medium shadow-[var(--sh-1)]"
                    value={rule.phoneNumber || ''}
                    onChange={(e) => updateRule(rule.id, 'phoneNumber', e.target.value)}
                    placeholder="+1 (415) 555-0100"
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={addRule}
          className="mt-3 inline-flex h-[34px] cursor-pointer items-center gap-1.5 rounded-[9px] border border-dashed px-3.5 text-[13px] font-semibold tracking-[-0.005em]"
          style={{
            background: 'var(--cream-1)',
            color: 'var(--set-green-deep)',
            borderColor: 'var(--set-green-soft)',
          }}
        >
          <Icon kind="plus" size={12} stroke={2} />
          {cfg.addLabel}
        </button>
      </div>
    </div>
  );
}

export interface ChannelPageSharedProps {
  capState: CapabilitiesState;
  setCapState: React.Dispatch<React.SetStateAction<CapabilitiesState>>;
}

export interface CallSettingsPageProps extends ChannelPageSharedProps {
  state: CallChannelState;
  setState: React.Dispatch<React.SetStateAction<CallChannelState>>;
  initial: CallChannelState;
}

export function CallSettingsPage({ state, setState, initial, capState, setCapState }: CallSettingsPageProps) {
  const setVerification = (v: VerificationState) => setState((s) => ({ ...s, verification: v }));
  const setRules = (rules: WarmTransferRule[]) =>
    setState((s) => ({ ...s, warmTransfer: { ...s.warmTransfer, rules } }));

  return (
    <>
      <SettingsPageHeader
        eyebrow="Channels · call"
        title="Phone"
        italic="channel"
        description="Voice, tone, and greeting come from Personality & character. This page covers what Elva can do, how she verifies callers, and where she hands off when she can't help."
      />
      <section className="set-fade-up set-d-1 mb-[22px]">
        <ChannelCapSlice
          channelId="call"
          channelLabel="phone calls"
          capabilitiesState={capState}
          setCapabilitiesState={setCapState}
        />
      </section>
      <section className="set-fade-up set-d-2 mb-[22px]">
        <ChannelVerification
          state={state.verification}
          setState={setVerification}
          initial={initial.verification}
          channelLabel="phone"
        />
      </section>
      <section className="set-fade-up set-d-3 mb-[22px]">
        <WarmTransferRules rules={state.warmTransfer.rules} setRules={setRules} kind="call" num="03" />
      </section>
    </>
  );
}

export interface SmsSettingsPageProps extends ChannelPageSharedProps {
  state: SmsChannelState;
  setState: React.Dispatch<React.SetStateAction<SmsChannelState>>;
  initial: SmsChannelState;
}

export function SmsSettingsPage({ state, setState, initial, capState, setCapState }: SmsSettingsPageProps) {
  const upd = (key: keyof SmsChannelState, value: SmsChannelState[keyof SmsChannelState]) =>
    setState((s) => ({ ...s, [key]: value }));
  const dirty = (key: keyof SmsChannelState) => state[key] !== initial[key];
  const setVerification = (v: VerificationState) => setState((s) => ({ ...s, verification: v }));
  const setRules = (rules: WarmTransferRule[]) =>
    setState((s) => ({ ...s, warmTransfer: { ...s.warmTransfer, rules } }));

  return (
    <>
      <SettingsPageHeader
        eyebrow="Channels · sms"
        title="SMS"
        italic="channel"
        description="Two-way text messaging. Patients reach Elva by texting your practice number; she responds in the same conversation thread. Quiet hours apply only to outbound."
      />
      <section className="set-fade-up set-d-1 mb-[22px]">
        <ChannelCapSlice
          channelId="sms"
          channelLabel="SMS"
          capabilitiesState={capState}
          setCapabilitiesState={setCapState}
        />
      </section>
      <section className="set-fade-up set-d-2 mb-[22px]">
        <ChannelVerification
          state={state.verification}
          setState={setVerification}
          initial={initial.verification}
          channelLabel="SMS"
        />
      </section>
      <section className="set-fade-up set-d-3 mb-[22px]">
        <WarmTransferRules rules={state.warmTransfer.rules} setRules={setRules} kind="sms-triage" num="03" />
      </section>
      <section className="set-fade-up set-d-4 mb-[22px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="04"
            title="SMS signature and timing"
            sub="The little details that make texts feel like they come from your practice."
          />
          <SettingsFieldRow
            title="Signature"
            description="Appended to outbound texts Elva initiates. Skip on replies in a continuous thread. Keep it short — every character costs."
            isDirty={dirty('signature')}
          >
            <input
              className="set-input w-[280px] shadow-[var(--sh-1)]"
              value={state.signature}
              onChange={(e) => upd('signature', e.target.value)}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Honor quiet hours for outbound texts"
            description="Pauses outbound SMS during the hours below. Inbound conversations are answered any time."
            isDirty={dirty('quietHoursEnabled')}
          >
            <SettingsToggle value={state.quietHoursEnabled} onChange={(v) => upd('quietHoursEnabled', v)} />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Quiet hours start"
            description="24-hour format. After this time, outbound texts queue until the end-time below."
            isDirty={dirty('quietHoursStart')}
          >
            <input
              className="set-input w-[120px] font-[var(--font-mono)] shadow-[var(--sh-1)]"
              value={state.quietHoursStart}
              onChange={(e) => upd('quietHoursStart', e.target.value)}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Quiet hours end"
            description="Queued outbound texts resume at this time."
            isDirty={dirty('quietHoursEnd')}
            last
          >
            <input
              className="set-input w-[120px] font-[var(--font-mono)] shadow-[var(--sh-1)]"
              value={state.quietHoursEnd}
              onChange={(e) => upd('quietHoursEnd', e.target.value)}
            />
          </SettingsFieldRow>
        </div>
      </section>
    </>
  );
}

export interface EmailSettingsPageProps extends ChannelPageSharedProps {
  state: EmailChannelState;
  setState: React.Dispatch<React.SetStateAction<EmailChannelState>>;
  initial: EmailChannelState;
}

export function EmailSettingsPage({ state, setState, initial, capState, setCapState }: EmailSettingsPageProps) {
  const upd = (key: keyof EmailChannelState, value: EmailChannelState[keyof EmailChannelState]) =>
    setState((s) => ({ ...s, [key]: value }));
  const dirty = (key: keyof EmailChannelState) => state[key] !== initial[key];
  const setVerification = (v: VerificationState) => setState((s) => ({ ...s, verification: v }));
  const setRules = (rules: WarmTransferRule[]) =>
    setState((s) => ({ ...s, warmTransfer: { ...s.warmTransfer, rules } }));

  return (
    <>
      <SettingsPageHeader
        eyebrow="Channels · email"
        title="Email"
        italic="channel"
        description="Inbound and outbound email handled by Elva. She acknowledges receipt quickly, drafts responses with full thread context, and creates Triage Inbox notes for anything outside her scope."
      />
      <section className="set-fade-up set-d-1 mb-[22px]">
        <ChannelCapSlice
          channelId="email"
          channelLabel="email"
          capabilitiesState={capState}
          setCapabilitiesState={setCapState}
        />
      </section>
      <section className="set-fade-up set-d-2 mb-[22px]">
        <ChannelVerification
          state={state.verification}
          setState={setVerification}
          initial={initial.verification}
          channelLabel="email"
        />
      </section>
      <section className="set-fade-up set-d-3 mb-[22px]">
        <WarmTransferRules
          rules={state.warmTransfer.rules}
          setRules={setRules}
          kind="email-silent"
          num="03"
        />
      </section>
      <section className="set-fade-up set-d-4 mb-[22px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="04"
            title="Email signature, response time, and acknowledgments"
            sub="How Elva signs her emails and how fast she replies."
          />
          <SettingsFieldRow
            title="Signature"
            description="Appended to every outbound email Elva sends. Multi-line is fine."
            isDirty={dirty('signature')}
          >
            <textarea
              className="set-textarea w-[320px] min-h-[92px] text-[13px]"
              value={state.signature}
              onChange={(e) => upd('signature', e.target.value)}
              rows={4}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Target response time"
            description="How quickly Elva aims to respond during business hours. After-hours emails get a response when the practice opens."
            isDirty={dirty('responseSLAMinutes')}
          >
            <SettingsSelect
              value={state.responseSLAMinutes}
              onChange={(v) => upd('responseSLAMinutes', v)}
              width={240}
              options={[
                { value: '5', label: 'Within 5 minutes' },
                { value: '15', label: 'Within 15 minutes' },
                { value: '30', label: 'Within 30 minutes' },
                { value: '60', label: 'Within 1 hour' },
                { value: '240', label: 'Within 4 hours' },
                { value: '480', label: 'Same business day' },
              ]}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Auto-acknowledge new emails"
            description="Send a quick 'We got your email and will respond soon' within 30 seconds of receipt."
            isDirty={dirty('autoAcknowledge')}
            last
          >
            <SettingsToggle value={state.autoAcknowledge} onChange={(v) => upd('autoAcknowledge', v)} />
          </SettingsFieldRow>
        </div>
      </section>
    </>
  );
}

export interface ChatSettingsPageProps extends ChannelPageSharedProps {
  state: ChatChannelState;
  setState: React.Dispatch<React.SetStateAction<ChatChannelState>>;
  initial: ChatChannelState;
}

export function ChatSettingsPage({ state, setState, initial, capState, setCapState }: ChatSettingsPageProps) {
  const upd = (key: keyof ChatChannelState, value: ChatChannelState[keyof ChatChannelState]) =>
    setState((s) => ({ ...s, [key]: value }));
  const dirty = (key: keyof ChatChannelState) => state[key] !== initial[key];
  const setVerification = (v: VerificationState) => setState((s) => ({ ...s, verification: v }));
  const setRules = (rules: WarmTransferRule[]) =>
    setState((s) => ({ ...s, warmTransfer: { ...s.warmTransfer, rules } }));

  return (
    <>
      <SettingsPageHeader
        eyebrow="Channels · chat"
        title="Web chat"
        italic="channel"
        description="Web chat widget on your practice website. The fastest channel — patients can ask, book, and reschedule from any page. Verification is lightest here."
      />
      <section className="set-fade-up set-d-1 mb-[22px]">
        <ChannelCapSlice
          channelId="chat"
          channelLabel="web chat"
          capabilitiesState={capState}
          setCapabilitiesState={setCapState}
        />
      </section>
      <section className="set-fade-up set-d-2 mb-[22px]">
        <ChannelVerification
          state={state.verification}
          setState={setVerification}
          initial={initial.verification}
          channelLabel="chat"
        />
      </section>
      <section className="set-fade-up set-d-3 mb-[22px]">
        <WarmTransferRules
          rules={state.warmTransfer.rules}
          setRules={setRules}
          kind="chat-triage"
          num="03"
        />
      </section>
      <section className="set-fade-up set-d-4 mb-[22px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="04"
            title="Welcome message and offline coverage"
            sub="The first thing patients see when they open the chat, and what happens when no one's around."
          />
          <SettingsFieldRow
            title="Welcome message"
            description="Shown the moment the chat widget opens. Patients are more likely to engage when the opener is specific and friendly."
            isDirty={dirty('welcomeMessage')}
          >
            <textarea
              className="set-textarea w-[360px] min-h-[70px]"
              value={state.welcomeMessage}
              onChange={(e) => upd('welcomeMessage', e.target.value)}
              rows={2}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Pre-chat form"
            description="Ask for the patient's name and phone before the conversation starts. Slightly more friction; useful if you want callable contact info."
            isDirty={dirty('preChatForm')}
          >
            <SettingsToggle value={state.preChatForm} onChange={(v) => upd('preChatForm', v)} />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="When the practice is closed"
            description="Elva is available 24/7, but if a patient wants a human and the front desk isn't staffed, this is what she offers instead."
            isDirty={dirty('offlineBehavior')}
            last
          >
            <SettingsSelect
              value={state.offlineBehavior}
              onChange={(v) => upd('offlineBehavior', v)}
              width={280}
              options={[
                { value: 'schedule-callback', label: 'Offer to schedule a callback' },
                { value: 'leave-message', label: 'Take a message via the chat form' },
                { value: 'phone-only', label: 'Direct to the phone line' },
              ]}
            />
          </SettingsFieldRow>
        </div>
      </section>
    </>
  );
}
